import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Award, Star } from 'lucide-react';

interface TopPerformersCardProps {
  topPerformers: Array<{
    productName: string;
    saferPercentage: number;
    riskScore: number;
  }>;
}

export const TopPerformersCard = ({ topPerformers }: TopPerformersCardProps) => {
  const getPerformanceBadge = (percentage: number) => {
    if (percentage >= 80) {
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">Excellent</Badge>;
    } else if (percentage >= 60) {
      return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">Good</Badge>;
    } else if (percentage >= 40) {
      return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200">Fair</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-800 hover:bg-red-200">Needs Improvement</Badge>;
    }
  };

  const getRiskTrend = (riskScore: number) => {
    if (riskScore <= 0.3) {
      return <TrendingDown className="h-4 w-4 text-green-600" />;
    } else if (riskScore <= 0.6) {
      return <TrendingUp className="h-4 w-4 text-yellow-600" />;
    } else {
      return <TrendingUp className="h-4 w-4 text-red-600" />;
    }
  };

  if (topPerformers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Top Performing Products
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              No performance data available. Add products with chemical data to see rankings.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5" />
          Top Performing Products
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {topPerformers.map((product, index) => (
            <div 
              key={product.productName}
              className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium text-sm line-clamp-1">{product.productName}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground">
                      {product.saferPercentage.toFixed(1)}% safer ingredients
                    </span>
                    {getRiskTrend(product.riskScore)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getPerformanceBadge(product.saferPercentage)}
              </div>
            </div>
          ))}
        </div>
        
        {topPerformers.length === 0 && (
          <div className="text-center py-6">
            <p className="text-muted-foreground text-sm">
              No products with performance data found.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};